import type { RegisterEvent, RegisterRecord } from '@/features/registers/api';

/**
 * RL-954 complaints in the register (kind `complaint`): complaints made in the
 * patient app arrive with `data.channel = 'app'`, a CMP case number, the
 * patient's category and contact preference, and two deadlines
 * (`acknowledgeBy`, `replyBy`, YYYY-MM-DD Malaysia time). `dueAt` / `overdue`
 * follow the next one (acknowledgement while New, the answer once
 * Acknowledged).
 */
export const COMPLAINT_KIND = 'complaint';

export type ComplaintSource = 'all' | 'app' | 'clinic';

export const SOURCE_OPTIONS: { value: ComplaintSource; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'app', label: 'From the app' },
  { value: 'clinic', label: 'Recorded by the clinic' },
];

const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

export const isAppComplaint = (r: Pick<RegisterRecord, 'data'>) =>
  r.data.channel === 'app';

export function matchesSource(r: RegisterRecord, source: ComplaintSource) {
  if (source === 'all') return true;
  return source === 'app' ? isAppComplaint(r) : !isAppComplaint(r);
}

/** The apps' complaint category (`appCategory`). */
export const APP_CATEGORY_LABEL: Record<string, string> = {
  waiting_time: 'Waiting time',
  billing: 'Billing',
  staff: 'Staff',
  my_care: 'My care',
  privacy: 'Privacy',
  app: 'The app',
};

export const CONTACT_PREFERENCE_LABEL: Record<string, string> = {
  app: 'In the app',
  phone: 'By phone',
  email: 'By email',
};

export interface ComplaintDeadline {
  /** "Acknowledge by" / "Answer by". */
  label: string;
  /** YYYY-MM-DD (Malaysia). */
  day: string;
  overdue: boolean;
}

/**
 * The deadline the complaint is working towards now (none once answered).
 * Overdue comes from the API (`overdue`, against `dueAt`).
 */
export function nextDeadline(r: RegisterRecord): ComplaintDeadline | null {
  if (r.closedAt) return null;
  const ack = str(r.data.acknowledgeBy);
  const reply = str(r.data.replyBy);
  if (r.status === 'new' && ack)
    return { label: 'Acknowledge by', day: ack, overdue: r.overdue };
  if (reply) return { label: 'Answer by', day: reply, overdue: r.overdue };
  return null;
}

export interface ComplaintDetail {
  appCategory: string | null;
  contactPreference: string | null;
  acknowledgeBy: string | null;
  replyBy: string | null;
  /** Last reply shown to the patient (the `reply` action). */
  reply: string | null;
  /** The answer (the `close` action). */
  response: string | null;
}

export function complaintDetail(r: RegisterRecord): ComplaintDetail {
  return {
    appCategory: str(r.data.appCategory),
    contactPreference: str(r.data.contactPreference),
    acknowledgeBy: str(r.data.acknowledgeBy),
    replyBy: str(r.data.replyBy),
    reply: str(r.data.reply),
    response: str(r.data.response),
  };
}

/** What an event said to the patient (reply / answer), and whether they were told. */
export function eventMessage(e: RegisterEvent): {
  text: string | null;
  notified: boolean;
} {
  const d = e.data ?? {};
  return {
    text: str(d.reply) ?? str(d.response),
    notified: d.patientNotified === true,
  };
}
